import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { Typography } from '@mui/material';
import theme from '@src/theme';
import { generatePath, Link } from 'react-router-dom';
import { Region } from '@src/constants/Regions';
import EmptyState from '@src/components/utilities/EmptyState/EmptyState';
import { Routes } from '@src/navigation/Routes';
import { useNavigationStore } from '@src/adapters/stores/useNavigationStore';
import OrganisationHospitalDTO from '@src/adapters/dto/hospital/OrganisationHospitalDTO';

const PREFIX = 'HospitalsTable';

const classes = {
  root: `${PREFIX}-root`,
  table: `${PREFIX}-table`,
  tableCell: `${PREFIX}-tableCell`,
  header: `${PREFIX}-header`,
  link: `${PREFIX}-link`,
};

const Root = styled('div')(({ theme: Theme }) => ({
  [`&.${classes.root}`]: {
    width: '100%',
  },

  [`& .${classes.table}`]: {
    width: '100%',
  },

  [`& .${classes.tableCell}`]: {
    paddingLeft: '16px',
  },
  [`& .${classes.link}`]: {
    color: 'inherit',
    textDecoration: 'none',
    '&:visited': {
      color: 'inherit',
    },
  },
}));

interface Props {
  hospitals: OrganisationHospitalDTO[];
  selectedPage: number;
  rowsPerPage: number;
  recordCount: number;
  changePage(value: number): void;
  changeRowsPerPage(value: number): void;
}

function HospitalsTable({
  hospitals,
  selectedPage,
  rowsPerPage,
  recordCount,
  changePage: setPage,
  changeRowsPerPage: setRowsPerPage,
}: Props) {
  const { currentOrganisation, setCurrentHospital } = useNavigationStore();

  const isEmpty = !hospitals || hospitals.length === 0;
  return (
    <Root className={classes.root}>
      <TableContainer>
        <Table
          className={classes.table}
          aria-labelledby="tableTitle"
          size="small"
          aria-label="Contents table">
          <TableHead className={classes.header}>
            <TableRow>
              <TableCell>
                <Typography
                  variant="subtitle1"
                  color={theme.palette.text.secondary}>
                  Hospital Name
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  variant="subtitle1"
                  color={theme.palette.text.secondary}>
                  Region
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  variant="subtitle1"
                  color={theme.palette.text.secondary}>
                  Code Set
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  variant="subtitle1"
                  color={theme.palette.text.secondary}>
                  Short Code
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isEmpty ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <EmptyState title="No hospitals to show!" />
                </TableCell>
              </TableRow>
            ) : (
              hospitals.map((hospital) => (
                <TableRow key={hospital.id}>
                  <TableCell>
                    <Link
                      to={generatePath(Routes.OrganisationHospitalUsers, {
                        organisationSlug: currentOrganisation?.name ?? null,
                        hospitalSlug: hospital.name ?? null,
                      })}
                      onClick={() => {
                        setCurrentHospital({
                          id: hospital.id ?? '',
                          name: hospital.name,
                          isActive: hospital.isActive,
                        });
                      }}
                      style={{
                        textDecoration: 'none',
                        color: theme.palette.primary.main,
                      }}>
                      <Typography variant="subtitle2">
                        {hospital.name}
                      </Typography>
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color={theme.palette.text.secondary}>
                      {Region[hospital.regionCode as keyof typeof Region]}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color={theme.palette.text.secondary}>
                      {hospital.codeSet}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color={theme.palette.text.secondary}>
                      {hospital.shortCode}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {!isEmpty && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={recordCount}
          rowsPerPage={rowsPerPage}
          page={selectedPage}
          onPageChange={(event, number) => setPage(number)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      )}
    </Root>
  );
}

export default HospitalsTable;
